import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Box, Button, Card, CardContent, Container, Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import borrowerLoanApi from '../../services/borrowerLoanApi';
import { storeBorrowerSession } from '../../services/borrowerSession';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';
import SectionHeader from '../../components/shared/SectionHeader';

const schema = z
  .object({
    loanNumber: z.string().trim().min(1, 'Loan number is required'),
    verifyBy: z.enum(['zip', 'phone']),
    zip: z.string().optional(),
    phoneLast4: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.verifyBy === 'zip') {
      if (!data.zip?.trim()) {
        ctx.addIssue({ code: 'custom', message: 'ZIP code is required', path: ['zip'] });
      } else if (!/^\d{5}(-\d{4})?$/.test(data.zip.trim())) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid ZIP code', path: ['zip'] });
      }
    } else if (!/^\d{4}$/.test(data.phoneLast4?.trim() || '')) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter the last 4 digits of your phone number',
        path: ['phoneLast4'],
      });
    }
  });

const StatusLookupPage = () => {
  const navigate = useNavigate();
  const [verifyBorrower, { isLoading }] = borrowerLoanApi.endpoints.verifyBorrower.useMutation();
  const [error, setError] = useState(null);

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      loanNumber: '',
      verifyBy: 'zip',
      zip: '',
      phoneLast4: '',
    },
  });

  const verifyBy = methods.watch('verifyBy');

  const onSubmit = async (values) => {
    setError(null);
    try {
      const result = await verifyBorrower({
        loanNumber: values.loanNumber.trim(),
        zip: values.verifyBy === 'zip' ? values.zip.trim() : undefined,
        phoneLast4: values.verifyBy === 'phone' ? values.phoneLast4.trim() : undefined,
      }).unwrap();

      const accessToken = result.access_token || result.accessToken;
      if (!accessToken) {
        throw new Error('Verification succeeded but no access token was returned.');
      }

      storeBorrowerSession({
        accessToken,
        loanNumber: values.loanNumber.trim(),
      });

      navigate('/status/loan', { replace: true });
    } catch (err) {
      setError(
        getAxiosErrorMessage(err, 'We could not verify your loan. Check your details and try again.'),
      );
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
      <Card>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <SectionHeader title="Check loan status" icon={<SearchIcon />} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Enter your loan number and verify with your property ZIP code or the last four digits of your phone.
          </Typography>

          <FormProvider methods={methods} onSubmit={onSubmit}>
            <Stack spacing={2}>
              <TextField name="loanNumber" label="Loan number" required size="small" />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Verify with
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={verifyBy}
                  onChange={(_, value) => {
                    if (value) methods.setValue('verifyBy', value, { shouldValidate: true });
                  }}
                  fullWidth
                >
                  <ToggleButton value="zip">ZIP code</ToggleButton>
                  <ToggleButton value="phone">Phone last 4</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {verifyBy === 'zip' ? (
                <TextField name="zip" label="Property ZIP code" required size="small" />
              ) : (
                <TextField
                  name="phoneLast4"
                  label="Phone last 4 digits"
                  required
                  size="small"
                  inputProps={{ maxLength: 4, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              )}

              {error && <Alert severity="error">{error}</Alert>}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? 'Verifying…' : 'View my loan'}
              </Button>
            </Stack>
          </FormProvider>
        </CardContent>
      </Card>
    </Container>
  );
};

export default StatusLookupPage;
